import type { RefObject } from "react";
import { Icon } from "@/lib/icons";
import { cn } from "@/lib/utils";

/**
 * Big play/pause button next to the waveform of the audio to handle. The
 * canvas comes from useAudioWaveform; the <audio> element stays in the page.
 *
 * `<TaskAudioPlayer isPlaying={isPlaying} onToggle={play} canvasRef={canvasRef} playLabel={t.playAudio} pauseLabel={t.pauseAudio} />`
 */
export function TaskAudioPlayer({
  isPlaying,
  onToggle,
  canvasRef,
  playLabel,
  pauseLabel,
  disabled = false,
  className,
}: {
  isPlaying: boolean;
  onToggle: () => void;
  canvasRef: RefObject<HTMLCanvasElement>;
  playLabel: string;
  pauseLabel: string;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg border p-2 transition-colors",
        isPlaying ? "border-brand/40 bg-brand/[0.04]" : "border-border/60 bg-surface-2",
        className,
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        aria-label={isPlaying ? pauseLabel : playLabel}
        aria-pressed={isPlaying}
        className="grid size-12 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-45"
      >
        <Icon name={isPlaying ? "pause" : "play"} className="size-5" />
      </button>
      {/* The canvas is taken out of the flow: its pixel width (CSS width x
          device ratio) would otherwise widen a grid column on every redraw. */}
      <div className="relative h-10 min-w-0 flex-1">
        <canvas ref={canvasRef} className="absolute inset-0 size-full cursor-pointer" aria-hidden="true" />
      </div>
    </div>
  );
}
